import React, { useState } from 'react';
import { Check, Gift } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { value: 'favourite', emoji: '⭐', label: 'A favourite of mine' },
  { value: 'helping', emoji: '🤝', label: "I'm just helping" },
  { value: 'want_title', emoji: '🎯', label: "I'm looking for this title" },
];

/**
 * Anonymous data-donation panel — "Help us build" section on landing page.
 * Donors drop title + barcode + optional reason. When reason = "want_title"
 * we reveal an email field and opt them into the title-alert marketing list
 * (funnel: anonymous donor → Hobbyist subscriber when we find their item cheap).
 */
export default function HelpUsBuildPanel() {
  const [title, setTitle] = useState('');
  const [barcode, setBarcode] = useState('');
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const showEmailField = reason === 'want_title';

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const body = { title, barcode, reason: reason || null };
      if (showEmailField && email) body.email = email;
      const res = await fetch(`${API}/api/donations/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(data.detail?.[0]?.msg || data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-[#050505]"
      id="help-us"
      data-testid="help-us-section"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700]/30 text-[#FFD700] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Gift className="w-4 h-4" /> Help Us Build
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Know a barcode? <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#00D26A] to-[#FFD700]">Donate it</span>
          </h2>
          <p className="text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed">
            Your favourite book, film, or CD — or a title you're looking for. Drop the title and the barcode and we'll add it to our master database. Everyone benefits.
          </p>
        </div>

        {status === 'success' ? (
          <div
            className="bg-gradient-to-r from-[#00D26A]/20 to-[#FFD700]/10 border border-[#00D26A]/40 rounded-2xl p-8 text-center"
            data-testid="donation-success"
          >
            <Check className="w-14 h-14 text-[#00D26A] mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-white mb-2">Thanks! 🙌</h3>
            <p className="text-zinc-300">
              {showEmailField && email
                ? "Check your email — we've sent a one-click link to join the Home Collection early-access list (30 days free, then £5/month)."
                : "Your donation is pending review — it'll land in our library soon."}
            </p>
            <button
              onClick={() => {
                setStatus(null); setTitle(''); setBarcode(''); setReason(''); setEmail('');
              }}
              className="mt-6 text-sm text-[#FFD700] hover:text-[#00D26A] underline"
              data-testid="donation-add-another"
            >
              Donate another →
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-[#0A0A0A] border border-zinc-800/60 rounded-2xl p-6 sm:p-8 space-y-5"
            data-testid="donation-form"
          >
            <div>
              <label htmlFor="help-title" className="block text-sm font-medium text-zinc-300 mb-2">Title</label>
              <input
                id="help-title"
                type="text"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="The Midnight Library"
                className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                data-testid="donation-title-input"
              />
            </div>

            <div>
              <label htmlFor="help-barcode" className="block text-sm font-medium text-zinc-300 mb-2">Barcode</label>
              <input
                id="help-barcode"
                type="text"
                required
                inputMode="numeric"
                pattern="[0-9]{10,13}"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="9781786892737"
                className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-[#FFD700] font-mono placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                data-testid="donation-barcode-input"
              />
              <p className="mt-1 text-xs text-zinc-500">10, 12, or 13 digits · usually on the back cover / packaging</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Why are you donating? <span className="text-zinc-500">(optional)</span></label>
              <div className="grid sm:grid-cols-3 gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setReason(reason === r.value ? '' : r.value)}
                    className={`px-4 py-3 rounded-lg border text-sm font-medium text-left transition-colors ${
                      reason === r.value
                        ? 'bg-[#00D26A]/10 border-[#00D26A]/60 text-[#00D26A]'
                        : 'bg-[#050505] border-zinc-800 text-zinc-400 hover:border-zinc-600'
                    }`}
                    data-testid={`donation-reason-${r.value}`}
                  >
                    <span className="mr-1">{r.emoji}</span> {r.label}
                  </button>
                ))}
              </div>
            </div>

            {showEmailField && (
              <div
                className="bg-[#FFD700]/5 border border-[#FFD700]/30 rounded-lg p-4"
                data-testid="donation-alert-optin"
              >
                <label htmlFor="help-email" className="block text-sm font-medium text-[#FFD700] mb-2">
                  🎯 We can alert you when we find the best price
                </label>
                <input
                  id="help-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com (optional)"
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FFD700]"
                  data-testid="donation-email-input"
                />
                <p className="mt-2 text-xs text-zinc-500">
                  We'll only email you about this title and our launch. Unsubscribe anytime.
                </p>
              </div>
            )}

            {status === 'error' && (
              <p className="text-sm text-red-400" data-testid="donation-error">
                ⚠️ {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-4 rounded-full bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-60 shadow-[0_0_30px_rgba(0,210,106,0.25)]"
              data-testid="donation-submit-btn"
            >
              {submitting ? 'Sending…' : 'Donate this title'}
            </button>

            <p className="text-center text-xs text-zinc-500">
              Anonymous by default · your data helps us launch faster
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
